import type React from "react";
import { useState, useEffect, useRef } from "react";
import { ChevronDownIcon, DesktopComputerIcon } from "@heroicons/react/outline";

interface LlmProviderDropdownProps {
  defaultValue?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const providers = [
  { value: "openai", label: "OpenAI" },
  { value: "mistral", label: "Mistral" },
];

const LlmProviderDropdown: React.FC<LlmProviderDropdownProps> = ({
  defaultValue = "openai",
  onChange,
  disabled = false,
}) => {
  const [selected, setSelected] = useState<string>(defaultValue);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) {
      setSelected(defaultValue);
    }
  }, [defaultValue, disabled]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value: string) => {
    setSelected(value);
    setOpen(false);
    onChange(value);
  };

  const displayText = providers.find((p) => p.value === selected)?.label ?? selected;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
          disabled
            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
            : "bg-white text-gray-600 border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer"
        }`}
      >
        <DesktopComputerIcon className="h-3 w-3" strokeWidth={2} />
        {displayText}
        {!disabled && (
          <ChevronDownIcon
            className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
            strokeWidth={2}
          />
        )}
      </button>
      {open && (
        <div className="absolute bottom-full mb-1 left-0 w-28 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
          {providers.map((provider) => (
            <button
              key={provider.value}
              type="button"
              onClick={() => handleSelect(provider.value)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors ${
                selected === provider.value
                  ? "text-green-600 font-semibold bg-green-50"
                  : "text-gray-700"
              }`}
            >
              {provider.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LlmProviderDropdown;
